import React from 'react';
import { ArrowLeft, Github, ExternalLink, BrainCircuit, Globe, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import architectureDiagram from '@/assets/achivements/architecture_diagram_hospital_prediction.jpg';
import uiHospital from '@/assets/achivements/ui_hospital.png';
import rg_hospital from '@/assets/achivements/rg_hospital.png';

const HospitalReadmissionPredictionSystem: React.FC = () => {
  return (
    <main className="min-h-screen bg-background">
      {/* Back Navigation */}
      <div className="max-w-6xl mx-auto px-4 pt-16 md:pt-20">
        <Link to="/" className="flex items-center text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Portfolio
        </Link>
      </div>
      
      {/* Hero Section */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col gap-4">
            <Badge variant="secondary" className="w-fit">Azure RMD Engineering College</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">Hospital Readmission Prediction System</h1>
            <div className="flex flex-wrap gap-2 my-4">
              <Badge variant="outline" className="bg-white/5 border-primary/20">React 19</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">FastAPI</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Python 3.12</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">XGBoost</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Random Forest</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">SHAP</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Azure</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Machine Learning</Badge>
            </div>
          </div>
        </div>
      </section>
      
      {/* Project Carousel */}
      <section className="py-6 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <div className="w-full overflow-hidden rounded-xl border border-border shadow-sm">
            <Carousel
              showThumbs={false}
              showStatus={false}
              infiniteLoop
              autoPlay
              interval={5000}
              className="project-carousel"
            >
              <div>
                <img src={architectureDiagram} alt="Hospital Readmission Prediction System Architecture" className="w-full object-cover" />
              </div>
              <div>
                <img src={rg_hospital} alt="Hospital Readmission Prediction System Architecture" className="w-full object-cover" />
              </div>
              <div>
                <img src={uiHospital} alt="Hospital Readmission Prediction Dashboard" className="w-full object-cover" />
              </div>
            </Carousel>
          </div>
        </div>
      </section>
      
      {/* Project Details */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="md:col-span-2 space-y-6">
              <div className="bg-card p-8 rounded-xl border border-border shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-foreground">Project Overview</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  A meta-classifier ensemble system that predicts hospital readmission risk with &gt;78% accuracy using advanced machine learning algorithms. This project addresses one of the costliest challenges facing healthcare systems by providing explainable AI insights with SHAP, dual-mode interface for manual and batch processing, and enterprise-grade Azure deployment with HIPAA-ready compliance.
                </p>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">How It Works</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  The system uses a two-level ensemble approach: Level 1 employs Random Forest, XGBoost, and Logistic Regression as base classifiers, while Level 2 uses a Logistic Regression meta-classifier to optimally combine predictions. SHAP provides explainable AI insights, and the system offers both manual entry and Excel batch processing modes for different operational needs.
                </p>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">Key Features</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-6">
                  <li>Meta-classifier ensemble with &gt;78% accuracy</li>
                  <li>SHAP explainable AI for transparent predictions</li>
                  <li>Dual input modes (manual + Excel batch processing)</li>
                  <li>Real-time risk stratification (Low/Medium/High)</li>
                  <li>Enterprise authentication with Firebase</li>
                  <li>Azure cloud deployment with auto-scaling</li>
                  <li>HIPAA-ready compliance and security</li>
                  <li>Interactive dashboard with visualizations</li>
                </ul>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">Technical Implementation</h3>
                <p className="text-muted-foreground leading-relaxed">
                  The solution uses React 19 for the frontend with Vite build tool and Tailwind CSS for styling. The backend is built with FastAPI and Python 3.12, incorporating XGBoost, Random Forest, and SHAP for machine learning. The system is deployed on Azure Static Web Apps with Azure App Service for the backend, ensuring enterprise-grade security and scalability. Firebase handles authentication, and the entire system is designed for HIPAA compliance.
                </p>
              </div>
              
              {/* Project demo */}
              <div className="bg-card p-8 rounded-xl border border-border shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-foreground">Project Demo</h2>
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe 
                    className="absolute top-0 left-0 w-full h-full rounded-lg"
                    src="https://www.youtube.com/embed/7C8SQVXLots?t=673" 
                    title="Hospital Readmission Prediction System Demo" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Project Links</h3>
                <div className="space-y-3">
                  <Button variant="outline" size="lg" className="w-full justify-start" asChild>
                    <a href="https://github.com/Mohan-Balaji/hospital-readmission-prediction" target="_blank" rel="noopener noreferrer" className="flex items-center">
                      <Github className="mr-2 h-5 w-5" />
                      <span>Source Code</span>
                    </a>
                  </Button>
                  <Button variant="outline" size="lg" className="w-full justify-start opacity-75 cursor-not-allowed" asChild disabled>
                    <div className="flex items-center">
                      <Globe className="mr-2 h-5 w-5" />
                      <span>Live Demo (Azure Paused)</span>
                    </div>
                  </Button>
                  <Button variant="outline" size="lg" className="w-full justify-start" asChild>
                    <a href="https://youtu.be/7C8SQVXLots?si=mpWdtpxOj6dIYLfQ&t=673" target="_blank" rel="noopener noreferrer" className="flex items-center">
                      <Play className="mr-2 h-5 w-5" />
                      <span>Video Demo</span>
                    </a>
                  </Button>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Technologies Used</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">R</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">React 19</p>
                      <p className="text-sm text-muted-foreground">Frontend Framework</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">F</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">FastAPI</p>
                      <p className="text-sm text-muted-foreground">Backend Framework</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">X</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">XGBoost</p>
                      <p className="text-sm text-muted-foreground">ML Algorithm</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">S</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">SHAP</p>
                      <p className="text-sm text-muted-foreground">Explainable AI</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">A</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Azure</p>
                      <p className="text-sm text-muted-foreground">Cloud Platform</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">P</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Python 3.12</p>
                      <p className="text-sm text-muted-foreground">Programming Language</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Project Impact</h3>
                <p className="text-muted-foreground mb-4">
                  Successfully reduced healthcare costs by enabling early intervention for high-risk patients through accurate readmission prediction. The system provides transparent AI insights that help healthcare providers make informed decisions and improve patient outcomes.
                </p>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Prediction Accuracy</span>
                    <span className="text-primary font-medium">&gt;78%</span>
                  </div>
                  <div className="h-2 w-full bg-muted/30 rounded-full overflow-hidden">
                    <div className="h-full w-[78%] bg-gradient-to-r from-primary to-purple-500 rounded-full"></div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Response Time</span>
                    <span className="text-primary font-medium">&lt;1s</span>
                  </div>
                  <div className="h-2 w-full bg-muted/30 rounded-full overflow-hidden">
                    <div className="h-full w-[100%] bg-gradient-to-r from-primary to-purple-500 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Related Projects */}
      <section className="py-12 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8 text-foreground">Other Projects You Might Like</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/Homelab_Cloud_Storage" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Homelab Cloud Storage</h3>
              <p className="text-muted-foreground line-clamp-2">A sophisticated self-hosted cloud storage solution leveraging TrueNAS Scale on repurposed enterprise hardware.</p>
            </Link>
            <Link to="/book-recommender" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">AI Book Recommender</h3>
              <p className="text-muted-foreground line-clamp-2">An intelligent book recommendation platform using machine learning algorithms for personalized suggestions.</p>
            </Link>
            <Link to="/recipe-generator-ai" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">AI Recipe Generator</h3>
              <p className="text-muted-foreground line-clamp-2">An intelligent recipe generation system that creates personalized meal plans based on available ingredients.</p>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HospitalReadmissionPredictionSystem;